import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import AdminLayout from '../../../components/AdminLayout';
import * as api from '../../../api/educentialApi';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    examBody: '',
    productName: '',
    description: '',
    category: 'SSCE',
    price: '',
    costPrice: '',
    apiProvider: '',
    icon: '',
    imageUrl: '',
    isActive: true
  });

  useEffect(() => {
    if (isEditMode) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.getScratchCardProduct(id);
      const product = response.data || response;
      setFormData({
        examBody: product.examBody || '',
        productName: product.productName || '',
        description: product.description || '',
        category: product.category || 'SSCE',
        price: product.price || '',
        costPrice: product.costPrice || '',
        apiProvider: product.apiProvider || '',
        icon: product.icon || '',
        imageUrl: product.imageUrl || '',
        isActive: product.isActive !== undefined ? product.isActive : true
      });
    } catch (err) {
      console.error('Error fetching product:', err);
      alert('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.examBody || !formData.productName || !formData.category || !formData.price || !formData.costPrice || !formData.apiProvider) {
      alert('Please fill in all required fields');
      return;
    }

    if (Number(formData.price) <= 0 || Number(formData.costPrice) <= 0) {
      alert('Price and cost price must be greater than 0');
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        price: Number(formData.price),
        costPrice: Number(formData.costPrice)
      };

      if (isEditMode) {
        await api.updateScratchCardProduct(id, submitData);
        alert('Product updated successfully!');
      } else {
        await api.createScratchCardProduct(submitData);
        alert('Product created successfully!');
      }

      navigate('/admin/scratch-cards');
    } catch (err) {
      console.error('Error saving product:', err);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} product: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode && !formData.examBody) {
    return (
      <AdminLayout title={isEditMode ? 'Edit Product' : 'Create Product'}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? 'Edit Scratch Card Product' : 'Create Scratch Card Product'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/scratch-cards')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Products
          </button>
          <h2 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Product' : 'Create New Product'}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exam Body <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="examBody"
                  value={formData.examBody}
                  onChange={handleChange}
                  placeholder="e.g., WAEC, NECO, JAMB, NABTEB"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  required
                >
                  <option value="SSCE">SSCE (Senior School Certificate)</option>
                  <option value="Admission">Admission</option>
                  <option value="Technical">Technical</option>
                  <option value="BECE">BECE (Basic Education Certificate)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                placeholder="e.g., WAEC Result Checker Card"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter product description..."
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">Pricing</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selling Price (₦) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="e.g., 1000"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cost Price (₦) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="costPrice"
                  value={formData.costPrice}
                  onChange={handleChange}
                  placeholder="e.g., 800"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  required
                />
              </div>
            </div>

            {formData.price && formData.costPrice && Number(formData.price) > 0 && Number(formData.costPrice) > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Profit Margin:</span> ₦{(Number(formData.price) - Number(formData.costPrice)).toLocaleString()}
                  ({(((Number(formData.price) - Number(formData.costPrice)) / Number(formData.price)) * 100).toFixed(1)}%)
                </p>
              </div>
            )}
          </div>

          {/* API Configuration */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">API Configuration</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Provider <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="apiProvider"
                value={formData.apiProvider}
                onChange={handleChange}
                placeholder="e.g., VTPass, Shago, BuyPower"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Provider name for the result checking API</p>
            </div>
          </div>

          {/* Display Settings */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">Display Settings</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon (Emoji)
                </label>
                <input
                  type="text"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  placeholder="e.g., 📋, 🎓, 📝"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  maxLength="2"
                />
                <p className="text-xs text-gray-500 mt-1">Single emoji to represent the product</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
                <p className="text-xs text-gray-500 mt-1">Optional product image URL</p>
              </div>
            </div>

            {formData.imageUrl && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Image Preview:</p>
                <img
                  src={formData.imageUrl}
                  alt="Preview"
                  className="h-32 w-auto rounded-lg border border-gray-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 border-b pb-2">Status</h3>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-5 w-5 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label className="ml-3 text-sm font-medium text-gray-700">
                Product is active and available for purchase
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin/scratch-cards')}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  {isEditMode ? 'Update Product' : 'Create Product'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ProductForm;
